// Общий поиск в шапке: одно поле на сделки, компании, контакты и суда.
//
// Почему не свой запрос на каждую сущность прямо здесь: useEntitySearch уже приводит
// четыре разных формата ядра к одному EntityRef (сделки и компании — сырой Bitrix,
// контакты — с колонкой full_name, суда — витрина). Здесь остаётся только сгруппировать.
//
// Что важно не потерять: ядро может ответить «не умею» (_core_unsupported) по одной
// сущности и нормально по остальным. Тогда честно пишем, какой раздел не искался, —
// иначе пользователь решит, что в нём ничего нет.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CoreUnsupported } from '../api/client'
import { useEntitySearch } from '../api/hooks'
import { ENTITIES, entityPath } from '../lib/entities'
import type { EntityKind, EntityRef } from '../lib/entities'

/** Пользователь печатает — незачем дёргать ядро (четырьмя запросами!) на каждую букву. */
const DEBOUNCE_MS = 300
/** Сколько строк показываем в группе. Полный список — в разделе. */
const PER_GROUP = 5
/** Короче ядро всё равно ничего осмысленного не найдёт (minQuery всех сущностей — 2). */
const MIN_Q = 2

const ORDER: EntityKind[] = ['deal', 'company', 'contact', 'vessel']

export function GlobalSearch() {
  const [text, setText] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const nav = useNavigate()

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text])

  // Клик мимо закрывает выпадашку — иначе она висит поверх страницы.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // Четыре хука подряд, а не в цикле: порядок и число хуков должны быть постоянными.
  const deal = useEntitySearch('deal', debounced)
  const company = useEntitySearch('company', debounced)
  const contact = useEntitySearch('contact', debounced)
  const vessel = useEntitySearch('vessel', debounced)
  const byKind = useMemo(
    () => ({ deal, company, contact, vessel }),
    [deal, company, contact, vessel],
  )

  const groups = useMemo(
    () => ORDER.map((kind) => ({
      kind,
      rows: (byKind[kind].data ?? []).slice(0, PER_GROUP),
      total: byKind[kind].data?.length ?? 0,
      error: byKind[kind].error,
    })),
    [byKind],
  )

  // Плоский список для стрелок: пользователь ходит по строкам, а не по группам.
  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups])
  useEffect(() => { setActive(0) }, [flat.length])

  // Список длиннее выпадашки: без этого стрелка уводит подсветку за нижний край,
  // и кажется, что она перестала двигаться.
  useEffect(() => {
    boxRef.current?.querySelector<HTMLElement>(`[data-row="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const q = debounced.trim()
  const tooShort = q.length > 0 && q.length < MIN_Q
  const asked = q.length >= MIN_Q
  // «Ничего не найдено» имеет право появиться, только когда ОТВЕТИЛИ ВСЕ четыре запроса.
  // Иначе получается ложь: пока часть запросов в пути (или отложена), пустота означает
  // «мы ещё не знаем», а не «такого нет».
  const answered = asked && ORDER.every((k) => byKind[k].isSuccess)
  const searching = asked && ORDER.some((k) => byKind[k].isPending && !byKind[k].error)
  const nothing = answered && flat.length === 0
  // Браузер считает, что сети нет: react-query такие запросы не выполняет, а откладывает —
  // они висят «в процессе» вечно. Без этой строки поиск выглядел бы просто зависшим.
  const offline = asked && ORDER.every((k) => byKind[k].fetchStatus === 'paused')

  // Отказ ядра — не то же самое, что «ничего не найдено»: разделяем.
  const refused = groups
    .filter((g) => g.error instanceof CoreUnsupported)
    .map((g) => ({ kind: g.kind, why: (g.error as CoreUnsupported).why }))
  const broken = groups.filter((g) => g.error && !(g.error instanceof CoreUnsupported))

  const go = (r: EntityRef) => {
    setText('')
    setDebounced('')
    setOpen(false)
    nav(entityPath(r.kind, r.id))
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return }
    if (!open || flat.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)) }
    if (e.key === 'Enter') { e.preventDefault(); go(flat[active]) }
  }

  // Сквозной номер строки: нужен, чтобы подсветка совпадала с плоским списком.
  let seen = -1

  return (
    <div className="gsearch" ref={boxRef}>
      <input
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Поиск по сделкам, компаниям, контактам и судам"
        aria-label="Общий поиск"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls="gsearch-drop"
      />

      {open && q.length > 0 && (
        <div className="gsearch-drop" id="gsearch-drop" role="listbox">
          {tooShort && <div className="picker-note muted">Введите хотя бы {MIN_Q} символа</div>}
          {offline
            ? <div className="picker-note err">Нет сети — запрос отложен до её появления.</div>
            : searching && <div className="picker-note muted">Ищем…</div>}

          {groups.map((g) => (
            g.rows.length === 0 ? null : (
              <div key={g.kind}>
                <div className="gsearch-group">{ENTITIES[g.kind].many}</div>
                {g.rows.map((r) => {
                  seen += 1
                  const i = seen
                  return (
                    <button
                      type="button"
                      key={`${g.kind}-${r.id}`}
                      role="option"
                      data-row={i}
                      aria-selected={i === active}
                      className={`picker-row${i === active ? ' active' : ''}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(r)}
                    >
                      <span className="picker-row-title">{r.title}</span>
                      <span className="muted picker-row-hint">
                        #{r.id}{r.hint ? ` · ${r.hint}` : ''}
                      </span>
                    </button>
                  )
                })}
                {/* Размер снят: `.picker-note` задаёт свой (--fs-small), и три соседние
                    строки того же выпадающего списка («Ничего не найдено», «Ищем…», отказы)
                    идут именно им. Инлайновые 12px делали эту одну строку мельче остальных
                    в том же списке. */}
                {g.total > g.rows.length && (
                  <div className="picker-note muted">
                    …и ещё {g.total - g.rows.length} — уточните запрос
                  </div>
                )}
              </div>
            )
          ))}

          {nothing && refused.length === 0 && broken.length === 0 && (
            <div className="picker-note muted">Ничего не найдено</div>
          )}

          {/* Ядро отказалось искать по этой сущности. Молча показать пустоту нельзя:
              «сделок нет» и «сделки не искались» — разные новости. */}
          {refused.map((r) => (
            <div key={r.kind} className="picker-note err">
              {ENTITIES[r.kind].many} не искались: ядро не умеет обслужить такой запрос
              {r.why ? ` (${r.why})` : ''}.
            </div>
          ))}
          {broken.map((g) => (
            <div key={g.kind} className="picker-note err">
              {ENTITIES[g.kind].many} не искались: ядро не ответило
              ({(g.error as Error).message}).
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
